import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Loader2, TrendingUp, Target, Swords, BrainCircuit } from "lucide-react";
import { TeamNewsPanel } from "@/components/TeamNewsPanel";

// Pro prediction card — xG → Poisson model (W/D/L, xG, scoreline, O/U 2.5, BTTS, H2H).
export const PredictionCard = ({ teamId, oppId, teamName, oppName, venue = "home", autoRun = false, venueSource }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [err, setErr] = useState(false);

  const runSeq = useRef(0);
  const run = () => {
    if (!teamId || !oppId) return;
    const seq = ++runSeq.current;   // a newer fixture/venue always wins — stale replies are dropped
    const fresh = () => seq === runSeq.current;
    setLoading(true); setErr(false); setDeepLoading(true); setData(null);
    // Phase 1 — FAST quantified read (no LLM): W/D/L, xG, scoreline, markets, H2H, belief + plain-English
    // explanation. Visible in ~3s so the coach sees the prediction immediately.
    api.get(`/predict/quick?team_id=${teamId}&opponent_id=${oppId}&venue=${venue}`)
      .then((r) => fresh() && setData(r.data))
      .catch(() => fresh() && setErr(true))
      .finally(() => fresh() && setLoading(false));
    // Phase 2 — the elite LLM tactical dossier (slower); merge the OCTON VERDICT in when it lands.
    api.get(`/predict/match?team_id=${teamId}&opponent_id=${oppId}&venue=${venue}`)
      .then((r) => fresh() && setData((prev) => ({ ...r.data, ...(prev || {}), dossier: r.data.dossier, explanation: r.data.explanation || prev?.explanation })))
      .catch(() => {})
      .finally(() => fresh() && setDeepLoading(false));
  };

  // Auto-run the moment the advisor hands us a fixture, so the belief read is visible without a click.
  useEffect(() => {
    if (autoRun && teamId && oppId) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, teamId, oppId, venue]);

  // Voice / TeamNewsPanel: the coach's team news changed → re-price this prediction.
  useEffect(() => {
    const h = () => { if (teamId && oppId) run(); };
    window.addEventListener("octon:team-news-changed", h);
    return () => window.removeEventListener("octon:team-news-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, oppId, venue]);

  const p = data?.prediction;
  const xg = data?.projected_xg || {};
  const xgVals = Object.values(xg);
  const m = data?.markets || {};
  const h = data?.head_to_head;

  return (
    <div className="card-tac mt-4 p-6" data-testid="prediction-card"
      data-octon-section="prediction predictive analysis analytics match forecast probability verdict"
      data-octon-label="Match Prediction" data-octon-state={data ? "ready" : loading ? "loading" : err ? "error" : "idle"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="txt-cyan" />
          <span className="font-tac text-[11px] tracking-[0.25em] text-white/85">MATCH PREDICTION</span>
          <span className="font-tac text-[9px] tracking-widest text-white/35">BAYESIAN DIXON-COLES</span>
        </div>
        <button data-testid="run-prediction-btn" onClick={run} disabled={loading || !teamId || !oppId}
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black transition hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] disabled:opacity-40">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Target size={13} />}
          {loading ? "Modelling…" : data ? "Re-run" : "Run prediction"}
        </button>
      </div>

      {!data && !loading && (
        <p className="mt-3 text-[12px] text-white/45">Run a Bayesian Dixon-Coles projection for {teamName || "your club"} vs {oppName || "the opponent"} — win/draw/loss, expected goals, most-likely scoreline, over/under &amp; BTTS.</p>
      )}

      {loading && <p className="mt-4 flex items-center gap-2 text-[12px] text-white/50"><Loader2 size={14} className="animate-spin txt-cyan" /> Building the quantified read from real form, xG &amp; head-to-head…</p>}

      {err && !loading && (
        <p className="mt-4 text-[12px] text-red-300" data-testid="pred-error">OCTON couldn't build the projection — check the connection and hit Run prediction again.</p>
      )}

      {data && p && (
        <div className="mt-4 space-y-4" data-testid="prediction-content">
          <p className="text-[13px] text-white/60">{data.fixture} · <span className="uppercase">{data.venue}</span>{venueSource && <span className="ml-2 rounded-md px-1.5 py-0.5 font-tac text-[9px] tracking-widest" style={{ background: venueSource === "real fixture" ? "#22d3ee22" : "#f59e0b22", color: venueSource === "real fixture" ? "#67e8f9" : "#fcd34d" }} data-testid="prediction-venue-source">VENUE · {venueSource.toUpperCase()}</span>}</p>
          {venueSource === "real fixture" && <p className="mt-1 text-[11px] text-white/40" data-testid="prediction-venue-note">Read for the real venue. Home-advantage is a large term in the Dixon-Coles model — a fixture read at the wrong venue can flip the favourite.</p>}

          {/* W/D/L bar */}
          <div>
            <div className="flex h-8 w-full overflow-hidden rounded-lg text-[11px] font-bold">
              <div style={{ width: `${p.win_pct}%` }} className="flex items-center justify-center bg-[#33d17a] text-black" data-testid="pred-win" title="Win">{p.win_pct}%</div>
              <div style={{ width: `${p.draw_pct}%` }} className="flex items-center justify-center bg-white/15 text-white" data-testid="pred-draw" title="Draw">{p.draw_pct}%</div>
              <div style={{ width: `${p.loss_pct}%` }} className="flex items-center justify-center bg-[#ff5a5a] text-black" data-testid="pred-loss" title="Loss">{p.loss_pct}%</div>
            </div>
            <div className="mt-1 flex justify-between font-tac text-[9px] tracking-widest text-white/40">
              <span>WIN</span><span>DRAW</span><span>LOSS</span>
            </div>
          </div>

          {/* stat chips */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="PROJ. xG" value={xgVals.length === 2 ? `${xgVals[0]} – ${xgVals[1]}` : "—"} />
            <Stat label="LIKELY SCORE" value={data.most_likely_scoreline || "—"} />
            <Stat label="OVER 2.5" value={m.over_2_5_pct != null ? `${m.over_2_5_pct}%` : "—"} />
            <Stat label="BTTS" value={m.btts_pct != null ? `${m.btts_pct}%` : "—"} />
          </div>

          {/* head-to-head */}
          {h?.played > 0 && (
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="pred-h2h">
              <p className="mb-1 flex items-center gap-1.5 font-tac text-[9px] tracking-widest text-white/40"><Swords size={10} /> HEAD-TO-HEAD · LAST {h.played}</p>
              <p className="text-[12px] text-white/70">{teamName} {h.team_wins}W · {h.draws}D · {h.opponent_wins}W {oppName}</p>
              {h.recent?.length > 0 && <p className="mt-1 text-[11px] text-white/40">{h.recent[0]}</p>}
            </div>
          )}

          {data.dossier?.verdict ? (
            <div>
              <p className="mb-1 font-tac text-[9px] tracking-widest txt-cyan">OCTON VERDICT</p>
              <p className="text-[13px] leading-relaxed text-white/80">{data.dossier.verdict}</p>
            </div>
          ) : deepLoading ? (
            <p className="flex items-center gap-2 text-[11px] text-white/40"><Loader2 size={12} className="animate-spin txt-cyan" /> OCTON's tactical verdict incoming…</p>
          ) : null}
          {data.belief && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] p-3" data-testid="pred-belief">
              <span className="font-tac text-[9px] tracking-widest txt-cyan">BAYESIAN BELIEF ENGINE</span>
              <span className="text-[11px] text-white/60">fitted on {data.belief.fitted_matches} real results{data.belief.xg_fed > 0 ? ` · ${data.belief.xg_fed} xG-fed` : ""}</span>
              {data.belief.brier != null && (
                <span className="rounded-full bg-white/8 px-2 py-0.5 font-mono text-[11px] text-white/75" title="Rolling 3-way Brier score of the belief engine — lower is better (0 = perfect, ~0.67 = random)">
                  calibration Brier {data.belief.brier}
                </span>
              )}
              {data.belief.engine_xg && (
                <span className="text-[10px] text-white/40">engine xG {data.belief.engine_xg.team}–{data.belief.engine_xg.opponent} (engine weight {Math.round((data.belief.blend_w ?? 0.5) * 100)}% · learned)</span>
              )}
              {data.belief.method === "hierarchical" && (
                <span className="rounded-full border border-amber-300/30 px-2 py-0.5 font-tac text-[9px] tracking-widest text-amber-200" data-testid="pred-hierarchical" title="Clubs from different leagues: each strength fitted in its own league, aligned by a league-strength prior (uncertainty widened)">
                  HIERARCHICAL CROSS-LEAGUE PRIOR · Δ {data.belief.league_offset > 0 ? "+" : ""}{data.belief.league_offset}
                </span>
              )}
            </div>
          )}
          {data.availability && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="pred-team-news"
              data-octon-section="team news absences priced in injuries availability" data-octon-label="Team News · priced in" data-octon-state="ready">
              <p className="font-tac text-[9px] tracking-widest txt-cyan">TEAM NEWS · PRICED INTO THE NUMBER</p>
              {[["team", data.team], ["opponent", data.opponent]].map(([k, name]) => {
                const a = data.availability[k] || {};
                return (
                  <div key={k} className="mt-1.5 text-[12px]" data-testid={`pred-absences-${k}`}>
                    <span className="text-white/85">{name}</span>{" "}
                    {a.out?.length ? (
                      <span className="text-white/60">without {a.out.map((r) => `${r.name} (${r.position}${r.replacement ? ` → ${r.replacement}` : ""})`).join(", ")} · own xG ×{a.own_xg_mult} · opp xG ×{a.opp_xg_mult}</span>
                    ) : <span className="text-white/45">— no regular starters ruled out in the current team-news sources</span>}
                    {(a.fringe_out?.length || a.not_counted?.length) ? (
                      <p className="mt-0.5 text-[10px] text-white/40" data-testid={`pred-fringe-${k}`}>
                        {a.fringe_out?.length ? `also out (not regular starters, no price impact): ${a.fringe_out.map((r) => r.name).join(", ")}` : ""}
                        {a.fringe_out?.length && a.not_counted?.length ? " · " : ""}
                        {a.not_counted?.length ? `not counted (available for this competition): ${a.not_counted.map((r) => r.name).join(", ")}` : ""}
                      </p>
                    ) : null}
                    {a.sources && (
                      <p className="mt-0.5 text-[10px] text-white/35" data-testid={`pred-sources-${k}`}>
                        sources · injury feed {a.sources.injury_feed_as_of ? new Date(a.sources.injury_feed_as_of).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        {" · "}live web {a.sources.live_web_checked_at ? new Date(a.sources.live_web_checked_at).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "not yet"}{a.sources.live_web_refreshing ? " (refreshing…)" : ""}
                        {a.sources.coach_overrides ? ` · ${a.sources.coach_overrides} coach override${a.sources.coach_overrides > 1 ? "s" : ""}` : ""}
                      </p>
                    )}
                  </div>
                );
              })}
              <TeamNewsPanel teamName={data.team} oppName={data.opponent} />
            </div>
          )}
          {data.explanation && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="pred-explanation">
              <p className="mb-1.5 flex items-center gap-1.5 font-tac text-[9px] tracking-widest txt-cyan"><BrainCircuit size={11} /> HOW OCTON READ IT</p>
              <p className="text-[13px] leading-relaxed text-white/75">{data.explanation}</p>
            </div>
          )}
          <p className="font-tac text-[9px] tracking-widest text-white/30">{data.model}</p>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/8 bg-black/40 p-3 text-center">
    <p className="font-head text-2xl font-black text-white">{value}</p>
    <p className="font-tac text-[8px] tracking-widest text-white/40">{label}</p>
  </div>
);
